/** The rules of the teacher sign-up form, apart from React and the network, so Node's own test
 * runner can load them (tests/signup.test.ts). The server (services/auth signup.py) is the
 * authority; these only stop a form that would certainly be refused from being sent, and turn
 * a refusal into the i18n key of a sentence that says what to do next. */

export type SignupPolicy = "open" | "off";

/** The server's minimum (TeacherSignupIn.password). */
export const MIN_PASSWORD = 8;

/** What GET /auth/v1/signup/teacher said. Anything unexpected (an older issuer without the
 * route answers 404, a gateway answers a page) reads as closed: the screen then explains how
 * an account is opened instead of offering a form the server would refuse. */
export function parsePolicy(body: unknown): SignupPolicy {
  return body !== null && typeof body === "object" && (body as { policy?: unknown }).policy === "open" ? "open" : "off";
}

export type SignupProblem = "email" | "password" | null;

/** The first thing the form would be refused for, checked the way the server checks it. */
export function signupProblem(form: { email: string; password: string }): SignupProblem {
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) return "email";
  if (form.password.length < MIN_PASSWORD) return "password";
  return null;
}

/** The code the issuer refuses a sign-in with while the address is not confirmed
 * (services/auth signup.py). Only a caller who typed the right password ever sees it. */
export const UNCONFIRMED = "email_unconfirmed";

export function isUnconfirmed(err: unknown): boolean {
  return err !== null && typeof err === "object" && (err as { code?: unknown }).code === UNCONFIRMED;
}

/** What asking for a new confirmation link came to. The server's 202 is the same for every
 * address, so "sent" means the ask was taken, never that an account exists. */
export type ResendOutcome = "sent" | "busy" | "failed";

export function resendOutcome(status: number): ResendOutcome {
  if (status === 202 || status === 200) return "sent";
  if (status === 429) return "busy";
  return "failed";
}

/** The i18n key under `teacherSignup.errors` for an HTTP status from the sign-up route. */
export function signupErrorKey(status: number): "exists" | "closed" | "busy" | "invalid" | "generic" {
  switch (status) {
    case 409:
      return "exists";
    case 403:
    case 404:
      return "closed";
    case 429:
      return "busy";
    case 422:
      return "invalid";
    default:
      return "generic";
  }
}
